import type { ReactNode } from "react";
import { EditBase, Form, useTranslate } from "ra-core";

import { ArrayInput } from "@/components/admin/array-input";
import { AutocompleteArrayInput } from "@/components/admin/autocomplete-array-input";
import { DateInput } from "@/components/admin/date-input";
import { ReferenceInput } from "@/components/admin/reference-input";
import { SelectInput } from "@/components/admin/select-input";
import { SimpleFormIterator } from "@/components/admin/simple-form-iterator";
import { TextInput } from "@/components/admin/text-input";
import { FormToolbar } from "../layout/FormToolbar";
import { useConfigurationContext } from "../root/ConfigurationContext";
import type { Sale } from "../types";
import { MANAGED_PLATFORMS, SERVICE_MODES } from "./clientModel";

export const ClientEdit = () => (
  <EditBase actions={false} redirect="show" mutationMode="pessimistic">
    <div className="mt-6 pb-8">
      <Form className="flex flex-col gap-4">
        <ClientInputs />
        <div className="rounded-[22px] bg-card px-5">
          <FormToolbar />
        </div>
      </Form>
    </div>
  </EditBase>
);

const FormSection = ({
  title,
  children,
}: {
  title: string;
  children: ReactNode;
}) => (
  <section className="rounded-[22px] bg-card p-5">
    <h2 className="mb-4 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
      {title}
    </h2>
    <div className="grid gap-4 md:grid-cols-2">{children}</div>
  </section>
);

const ClientInputs = () => {
  const translate = useTranslate();
  const { companySectors, dealCategories } = useConfigurationContext();

  return (
    <>
      <FormSection title={translate("resources.clients.forcedCaseName")}>
        <TextInput source="name" helperText={false} />
        <SelectInput
          source="sector"
          choices={companySectors}
          optionText="label"
          optionValue="value"
          helperText={false}
        />
        <TextInput source="website" helperText={false} />
        <DateInput source="client_since" helperText={false} />
      </FormSection>

      <FormSection title={translate("resources.clients.sections.service")}>
        <SelectInput
          source="service_mode"
          choices={SERVICE_MODES.map((mode) => ({
            id: mode,
            name: translate(`resources.clients.service_modes.${mode}`),
          }))}
          helperText={false}
        />
        <ReferenceInput source="sales_id" reference="sales">
          <SelectInput
            optionText={(sale: Sale) => `${sale.first_name} ${sale.last_name}`}
            helperText={false}
          />
        </ReferenceInput>
        <div className="md:col-span-2">
          <AutocompleteArrayInput
            source="services"
            choices={dealCategories}
            optionText="label"
            optionValue="value"
            helperText={false}
          />
        </div>
      </FormSection>

      <FormSection title={translate("resources.clients.sections.contact")}>
        <TextInput source="main_contact_name" helperText={false} />
        <TextInput source="main_contact_role" helperText={false} />
        <TextInput
          source="main_contact_whatsapp"
          placeholder="+57 300 000 0000"
          helperText={false}
        />
        <TextInput
          source="main_contact_email"
          type="email"
          helperText={false}
        />
        <TextInput source="content_approver" helperText={false} />
      </FormSection>

      <section className="rounded-[22px] bg-card p-5">
        <h2 className="mb-1 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
          {translate("resources.clients.sections.accounts")}
        </h2>
        <p className="mb-4 text-sm text-muted-foreground">
          {translate("resources.clients.accounts.no_passwords")}
        </p>
        <ArrayInput source="managed_accounts" label={false} helperText={false}>
          <SimpleFormIterator inline disableReordering>
            <SelectInput
              source="platform"
              label={translate("resources.clients.accounts.platform")}
              choices={MANAGED_PLATFORMS}
              helperText={false}
            />
            <TextInput
              source="handle"
              label={translate("resources.clients.accounts.handle")}
              helperText={false}
            />
            <TextInput
              source="notes"
              label={translate("resources.clients.accounts.notes")}
              helperText={false}
            />
          </SimpleFormIterator>
        </ArrayInput>
      </section>

      <section className="rounded-[22px] bg-card p-5">
        <h2 className="mb-4 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
          {translate("resources.clients.sections.notes")}
        </h2>
        <TextInput
          source="client_notes"
          multiline
          rows={5}
          helperText={false}
        />
      </section>
    </>
  );
};
